// @givmo/payments — the SHARED double-charge retry classifier (R-20 hoist of the ×3 app copies).
//
// ONE definition of "is a failed charge safely retryable WITH THE SAME token + idempotency key",
// shared by the donation, gift-card and pledge checkouts (donate), the Connect pay sheet (pay), and
// the employer charge dialogs (admin + the company console). Each surface re-POSTs the SAME
// instrument under the SAME `Idempotency-Key` on a retryable error so the backend's idempotency
// REPLAYS the original charge (never double-charges); a FATAL error instead forces a fresh instrument
// (a new token → a new key) and must NOT reuse the key.
//
// The classifier is intentionally conservative — "retryable" is the SAFE DEFAULT because a same-key
// retry is at worst a de-duped no-op (the backend de-dupes on the key), while a wrong "fatal" verdict
// clears the held instrument → mints a new key → DOUBLE-CHARGES if the original actually went through.
// That includes the idempotency seam's own 409s (CAM-R59): a key already in use means the request
// that first used it may still be charging, so it is never a definite no-charge.

import { firstErrorCode, type GivmoError } from '@givmo/api-client';

/**
 * The idempotency-seam 409 codes that mean a request under THIS key already exists server-side and
 * may charge — keyed on the code, never on the bare 409, which also carries definite no-charges
 * (insufficient funds, a stale quote or disclosure, `intent_already_completed`):
 *   • `IDEMPOTENCY_IN_PROGRESS` (consumer seam) — the first request under the key is still running;
 *     a same-key retry answers its stored outcome once it finishes.
 *   • `IDEMPOTENCY_KEY_REUSED` (consumer seam) — the key arrived with a DIFFERENT body. The seam
 *     compares the body before it checks the in-flight status (backend `app/api/idempotency.py`
 *     `_claim_idempotency`). It would store a normally returned 2xx or 4xx (`is_cacheable_status`),
 *     but every consumer charge route RAISES its 4xx, and a raise releases the key
 *     (`IdempotentAPIRoute`; backend `agent_docs/domain/donations.md` "Semantics"), so only a 2xx
 *     is ever stored: the request that first used the key is either still running or succeeded.
 *     The backend's other two senders of this code (the recurring-create and Connect re-advice
 *     guards) also answer only after a committed request, and no web charge flow calls either.
 *   • `conflict_in_progress` (Connect seam, the pay sheet) — the first confirm is still running.
 * Connect's `idempotency_conflict` is deliberately NOT here: Connect also stores a finalized 4xx (so
 * it can follow a recorded decline), and the sheet charges at most once per intent, so a fresh key
 * there can never charge twice.
 */
const IDEMPOTENCY_KEY_IN_USE_CODES: ReadonlySet<string> = new Set([
  'IDEMPOTENCY_IN_PROGRESS',
  'IDEMPOTENCY_KEY_REUSED',
  'conflict_in_progress',
]);

/**
 * Whether a failed charge is the idempotency seam refusing a key that is already in use (see
 * {@link IDEMPOTENCY_KEY_IN_USE_CODES}): a MAYBE-CHARGED outcome, never a definite no-charge. The
 * surfaces read it to swap the server's technical sentence for their own maybe-charged copy;
 * {@link isRetryableChargeError} already classifies it retryable.
 */
export function isIdempotencyKeyInUse(error: GivmoError): boolean {
  if (error.status !== 409) return false;
  const code = firstErrorCode(error);
  return code !== null && IDEMPOTENCY_KEY_IN_USE_CODES.has(code);
}

/**
 * Whether a failed CHARGE is safely retryable WITH THE SAME token + idempotency key.
 *
 * RETRYABLE (re-POST the same instrument under the same key — the backend idempotently replays):
 *   • HTTP **408** Request Timeout — the request may have reached the server AND charged before the
 *     timeout; we must reuse the same key so a retry de-dupes rather than double-charges.
 *   • HTTP **409** whose code says the key is already in use ({@link isIdempotencyKeyInUse}) — the
 *     first request under it may still charge; a retry under the same key answers its outcome, a
 *     fresh key would charge a second time.
 *   • HTTP **429** Too Many Requests — the velocity cap rejected this attempt BEFORE charging, so a
 *     later retry is safe (and harmless either way: the same key de-dupes).
 *   • HTTP **5xx** — the server failed mid-request; the charge may have succeeded server-side.
 *   • **no status** (`typeof status !== 'number'`) — transport/timeout/offline/abort, OR a 2xx the
 *     client could not confirm (`unconfirmed()`), where the charge most likely SUCCEEDED. Defaulting
 *     these to fatal would clear the held card → a new key → a double-charge; retryable is strictly
 *     safer (worst case the user retries a hopeless charge and sees the same error — no double money).
 *
 * FATAL (force a fresh instrument → a new key):
 *   • any OTHER explicit 4xx (402 decline, 422 validation, 400 business — incl. the admin over-budget
 *     `COMPANY_BUDGET_EXCEEDED`, any other 409, 401/403 auth / App-Check) — a definite client-side
 *     rejection where the charge did NOT execute.
 */
export function isRetryableChargeError(error: GivmoError): boolean {
  const status = error.status;
  if (typeof status !== 'number') return true; // network / unknown / unconfirmed-2xx → retryable.
  if (isIdempotencyKeyInUse(error)) return true; // the first request under the key may still charge.
  // Transient explicit statuses are retryable; everything else with a 4xx status is fatal.
  return status === 408 || status === 429 || status >= 500;
}

/**
 * CAM-R99's refusal (D-CAM-87): a 503 that stops THIS attempt before any charge, because an EARLIER
 * attempt for the same match, under another key, may have captured and has not settled. Maybe-charged
 * all the same: the code says nothing about this attempt's own key, so a surface keeps the key.
 */
export const EARLIER_PAYMENT_SETTLING_CODE = 'EARLIER_PAYMENT_SETTLING';

/**
 * The maybe-charged refusals a money route words for its payer (backend `app/domain/maybe_charged.py`):
 * a 503 whose `error_code` is one of these and whose `detail` is a sentence written to be shown —
 * D-CAM-45's donor words, D-CAM-79's company words, D-CAM-90's settling words. The seam's key-in-use
 * codes carry those words only at 503, on a route that moves money; their 409 form elsewhere carries a
 * technical sentence.
 */
const SERVER_WORDED_MAYBE_CHARGED_CODES: ReadonlySet<string> = new Set([
  'CHARGE_MAY_HAVE_CAPTURED',
  EARLIER_PAYMENT_SETTLING_CODE,
  'IDEMPOTENCY_IN_PROGRESS',
  'IDEMPOTENCY_KEY_REUSED',
]);

/** A sentence ends with its own stop, so the hint after it is a sentence of its own. */
function asSentence(text: string): string {
  return /[.!?…]$/.test(text) ? text : `${text}.`;
}

/**
 * The line a money dialog shows for a RETRYABLE failure ({@link isRetryableChargeError}): the server's
 * own sentence where the server worded one for the payer, never a processor's (EP-WEB34). Chosen by
 * what the answer says — its status, its code, whether it came at all — and never by its text:
 *   • CAM-R99's `EARLIER_PAYMENT_SETTLING`: the server's sentence ALONE. It says that this attempt was
 *     stopped and that an earlier one may have gone through; the surface's "could not confirm … do not
 *     assume it failed" would contradict it (Company Profiles, 2026-10-03).
 *   • another maybe-charged refusal worded for the payer: its sentence, then the surface's `hint`.
 *   • no answer at all (`kind: 'network'`): the client's own sentence for it, then the hint.
 *   • anything else: the hint alone. That covers a processor's failure (the 502's `detail` is the
 *     processor's own text, "Error 500: …"), a bare 5xx's title, a timeout, the velocity cap and an
 *     unconfirmed 2xx.
 * Only the words differ: every one of these is retryable, so the surface keeps its key and its
 * maybe-charged state whichever line it shows.
 */
export function maybeChargedLine(error: GivmoError, hint: string): string {
  const code = error.status === 503 ? firstErrorCode(error) : null;
  const detail = error.entries[0]?.detail?.trim() ?? '';
  if (code !== null && SERVER_WORDED_MAYBE_CHARGED_CODES.has(code) && detail.length > 0) {
    return code === EARLIER_PAYMENT_SETTLING_CODE
      ? asSentence(detail)
      : `${asSentence(detail)} ${hint}`;
  }
  if (error.kind === 'network' && error.message.trim().length > 0) {
    return `${asSentence(error.message.trim())} ${hint}`;
  }
  return hint;
}

/** The Connect envelope `code` for an intent that already reached the terminal `succeeded` state. */
export const INTENT_ALREADY_COMPLETED_CODE = 'intent_already_completed';

/**
 * Whether this error is the CT-BE8 succeeded carve-out: a **409** whose Connect
 * envelope `code` is `intent_already_completed`. That code is revealed ONLY after
 * a VALID `client_secret` match on an intent already in the terminal `succeeded`
 * state (the charge completed + was booked); the client contract is to treat it
 * as TERMINAL SUCCESS and re-read the intent for its outcome — NOT a generic
 * error and NOT a fatal instrument-clear.
 *
 * This is NOT a retry: it must NOT re-mint a fresh idempotency key (a re-confirm
 * under the SAME key byte-replays the original success on the backend before this
 * 409 ever fires; only a genuinely NEW confirm of an already-succeeded intent
 * lands here). So {@link isRetryableChargeError} deliberately still classifies this
 * 409 as FATAL — this predicate is checked FIRST, ahead of that split, to
 * divert the success case out of the error paths entirely. Consumed by the Connect
 * pay sheet (pay); harmless for the surfaces that never see a Connect 409.
 */
export function isIntentAlreadyCompleted(error: GivmoError): boolean {
  return error.status === 409 && error.connectCode === INTENT_ALREADY_COMPLETED_CODE;
}
